import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	useTheme
} from "@mui/material";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {format} from "date-fns";
import {CustomCellRendererProps} from "ag-grid-react";
import {CloudUpload, Downloading} from "@mui/icons-material";
import ServerSideDataTable from "@components/ServerSideDataTable";
import {styled} from "@mui/material/styles";
import Papa from "papaparse";
import {useSnackbar} from "notistack";
import {Link} from "react-router-dom";
import {urls} from "@routes";

type ContactListStatus = "uploading" | "active";

interface ContactList {
	uid: string
	name: string
	created_on: number
	status: ContactListStatus
	contacts_count: number
	column_count: number
}

interface PageData {
	status_code: string
	status_text: string

	user_id: number
	contact_lists: ContactList[]
}

interface WebhookMessage {
	event_type: string
	event_data: any
}


export default function ContactLists() {
	const theme = useTheme();
	const ws = useRef<WebSocket | null>(null);

	const [userId, setUserId] = useState<number | null>(null);
	const [contactLists, setContactLists] = useState<ContactList[]>([]);
	const [openCsvImportDialog, setOpenCsvImportDialog] = useState<boolean>(false);

	// Page Title
	useEffect(() => {
		document.title = "Contact Lists - Deliveryman.ai";
	}, []);

	// Fetch the page data.
	const pageDataQuery = useQuery({
		queryKey: ["emailAccounts"],
		queryFn: () => authenticateAndFetchData("/contact-lists/"),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (pageDataQuery.data) {
			let data = pageDataQuery.data.data as PageData
			setContactLists(data.contact_lists);
			setUserId(data.user_id);
		}
	}, [pageDataQuery.data]);

	// Websocket code
	useEffect(() => {
		const wsUrl = `${process.env.REACT_APP_WS_HOST}/ws/contact-list/${userId}/`;

		// Initialize WebSocket connection
		if (userId) {
			ws.current = new WebSocket(wsUrl);

			ws.current.onopen = () => {
				console.log("WebSocket connection established");
			};

			ws.current.onmessage = (event) => {
				const data: WebhookMessage = JSON.parse(event.data)["event_data"];
				const eventName: string = data["event_name"];
				const eventData: any = data["event_data"];

				if (eventName === "contact_list_status_update") {
					const contactListUID: string = eventData["contact_list_uid"];
					const newStatus: ContactListStatus = eventData["new_status"];
					const totalContacts: number = eventData["total_contacts"];
					const totalColumns: number = eventData["total_columns"];

					setContactLists(prevItems => prevItems.map(contactList => {
						return contactList.uid === contactListUID ? {
							...contactList,
							status: newStatus,
							contacts_count: totalContacts,
							column_count: totalColumns
						} : contactList
					}));
				} else {
					console.log("unhandled ws event:", event);
				}
			}

			ws.current.onclose = () => {
				console.log("WebSocket connection closed");
			};

			ws.current.onerror = (error) => {
				console.error("WebSocket error:", error);
			};
		}
	}, [userId]);

	function closeCsvImportDialog() {
		setOpenCsvImportDialog(false);
	}

	/**
	 * Call this function when csv file import API call has completed successfully.
	 */
	function csvContactListImportSuccessful() {
		pageDataQuery.refetch().then();
		setOpenCsvImportDialog(false);
	}

	// --------------------- TABLE SETUP ---------------------

	const ContactListDetailsLink = (customProps: CustomCellRendererProps) => {
		return (
			<Link to={urls["contactListDetails"].replace(":contactListUID", customProps.data["uid"])}
						style={{color: theme.palette.text.primary}}>
				{customProps.data["name"]}
			</Link>
		);
	}

	function timestampFormatter(params: ValueFormatterParams) {
		return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
	}

	const StatusBadgeCell = (customProps: CustomCellRendererProps) => {
		if (customProps.value === "uploading") {
			return <Chip size={"small"} color={"default"} label={"Uploading..."}/>

		} else if (customProps.value === "active") {
			return <Chip size={"small"} color={"success"} label={"Active"}/>

		} else if (customProps.value === "failed") {
			return <Chip size={"small"} color={"error"} label={"Failed"}/>

		} else {
			return <Chip size={"small"} color={"secondary"} label={"N/A"}/>
		}
	}

	const ImportCsvContactListButton = () => {
		return (
			<Button startIcon={<Downloading/>}
							variant="contained"
							color="primary"
							onClick={() => {
								setOpenCsvImportDialog(true);
							}}>
				Import From CSV File
			</Button>
		)
	}

	// Columns for contact lists table.
	const columnDefs: ColDef[] = [
		{field: "name", headerName: "Name", cellRenderer: ContactListDetailsLink},
		{field: "created_on", headerName: "Imported On", valueFormatter: timestampFormatter, flex: 2},
		{field: "contacts_count", headerName: "Total Contacts"},
		{field: "column_count", headerName: "Total Columns"},
		{field: "status", headerName: "Status", cellRenderer: StatusBadgeCell, sortable: false, resizable: false},
	]

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading || pageDataQuery.isRefetching) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else {
		return (
			<Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"column"} spacing={1}>
					<Typography variant={"h4"} fontWeight={"bold"} align={"center"} color={"primary"}>
						Your Contact Lists
					</Typography>
					<Typography variant={"body1"} align={"center"}>
						Manage all your imported contact lists here.<br/>
						You can view or delete existing lists, as well as import new ones from a CSV file.
					</Typography>
				</Stack>
				<Box sx={{height: "100%"}}>
					<ServerSideDataTable columns={columnDefs}
															 rows={contactLists}
															 actions={ImportCsvContactListButton()}/>
				</Box>
				<ImportContactListDialog open={openCsvImportDialog}
																 close={closeCsvImportDialog}
																 success={csvContactListImportSuccessful}/>
			</Stack>
		)
	}
}

function ImportContactListDialog(props: {
	open: boolean,
	close: () => void,
	success: () => void,
}) {
	const {enqueueSnackbar} = useSnackbar();

	const [listName, setListName] = useState<string>("");
	const [file, setFile] = useState<File | null>(null);
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [contactsData, setContactsData] = useState<string[][]>([]);
	const [emailColumnIndex, setEmailColumnIndex] = useState<number>(0);

	// Mutation for adding contact list.
	const addContactListMutation = useMutation({
		mutationKey: ["addContactListMutation"],
		mutationFn: () => authenticateAndPostData("/add-contact-list/", {
			name: listName.trim(),
			csv_data: contactsData,
			csv_headers: csvHeaders,
			email_column_index: emailColumnIndex,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			enqueueSnackbar("Success! Your new contact list is being created.", {
				variant: "success",
			});
			props.success();
		},
		onError: (error: ApiRequestFailed) => {
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		},
	})

	// This will parse the CSV file whenever a new file is selected.
	useEffect(() => {
		if (file) {
			file.text().then(value => {
				let parseResult = Papa.parse(value, {
					skipEmptyLines: "greedy",
				});
				let csvData = parseResult.data as Array<Array<string>>;
				csvData = csvData.filter(value => value.length)

				// Get the headers from first row.
				setCsvHeaders(csvData[0]);
				setContactsData(csvData.slice(1));
			});
		}
	}, [file]);

	const VisuallyHiddenInput = styled('input')({
		clip: 'rect(0 0 0 0)',
		clipPath: 'inset(50%)',
		height: 1,
		overflow: 'hidden',
		position: 'absolute',
		bottom: 0,
		left: 0,
		whiteSpace: 'nowrap',
		width: 1,
	});

	function onFileSelected(event: ChangeEvent<HTMLInputElement>) {
		if (event.target.files) {
			setFile(event.target.files[0]);
		}
	}

	const EmailColumnSelectionForm = () => (
		<FormControl sx={{mt: 4}} fullWidth>
			<InputLabel id="col-select-label">Email Column Name</InputLabel>
			<Select
				labelId="col-select-label"
				value={emailColumnIndex}
				label="Email Column Name"
				onChange={e => setEmailColumnIndex(e.target.value as number)}
			>
				{csvHeaders.map((header, index) => {
					return (
						<MenuItem key={index} value={index}>{header}</MenuItem>
					);
				})}
			</Select>
		</FormControl>
	)

	function confirmButtonHandler() {
		addContactListMutation.mutate();
	}

	return (
		<Dialog open={props.open}>
			<DialogTitle>Import Contact List from CSV File</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Upload a CSV with maximum 100,000 rows & 20 columns.
				</DialogContentText>
				<Stack direction={"column"} spacing={1} sx={{mt: 2}}>
					<Typography variant={"subtitle1"} sx={{fontWeight: "bold"}}>
						Contact List Name:
					</Typography>
					<TextField value={listName}
										 onChange={e => setListName(e.target.value)}
										 variant="outlined"
										 required/>
				</Stack>
				<Stack display={"flex"}
							 flexDirection={"column"}
							 justifyContent={"flex-start"}
							 alignItems={"flex-start"}
							 sx={{mt: 4}}>
					<Button
						component="label"
						role={undefined}
						variant="contained"
						color={"primary"}
						disabled={addContactListMutation.isPending}
						tabIndex={-1}
						startIcon={<CloudUpload/>}
						size={"small"}
					>
						Upload .CSV File
						<VisuallyHiddenInput
							type="file"
							accept={".csv"}
							onChange={onFileSelected}
							multiple
						/>
					</Button>
					<Typography variant="subtitle2" align={"center"} sx={{mt: 2}}>
						{file ? `Selected File: ${file.name}` : "No Files Uploaded"}
					</Typography>
					{(file !== null) && <EmailColumnSelectionForm/>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.close} disabled={addContactListMutation.isPending}>Cancel</Button>
				<Button disabled={(file === null) || (listName.trim() === "") || addContactListMutation.isPending}
								onClick={confirmButtonHandler}>
					{addContactListMutation.isPending ? "Uploading. Please Wait..." : "Confirm"}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
